import { request } from '@/common/request.js';
import { getStorageSync } from '@/utils/storage.js';
import { getPersistentDeviceID } from '@/utils/utils';

/** 埋点共用设备标识生成任务，避免并发上报时重复生成浏览器指纹。 */
let trackingDeviceIdPromise = null;

class Analytics {
	/**
	 * 通用埋点上报
	 * @param {string} url - 数据上报的 API 地址
	 */
	constructor(url) {
		this.url = url;
	}

	/**
	 * 发送埋点数据
	 * @param {Object} data - 埋点数据
	 * @param {string} [data.deviceId] - 设备标识
	 * @param {string|number} [data.userId] - 登录用户 ID
	 * @param {string} [data.companyCode] - 公司编码
	 * @param {string} [data.companyName] - 公司名称
	 * @param {string} [data.module] - 模块名称
	 * @param {string} [data.eventCode] - 事件编码
	 * @param {string} [data.eventName] - 事件名称
	 * @param {string} [data.route] - 路由
	 * @param {string} [data.attrName] - 属性名称
	 * @param {string} [data.attrCode] - 属性值
	 * @param {number} [data.sourceSign] - 入口来源：1 网页端，2 移动端，3 cmdc 入口
	 * @returns {Promise}
	 */
	sendData(data) {
		return request({ url: this.url, method: 'POST', data })
			.then(res => {
				if (res.code != 200) {
					return Promise.reject(new Error('Tracking request failed'));
				}
				return res;
			})
			.catch(error => {
				console.error('Error sending data via request:', error);
				throw error;
			});
	}
}

const defaultReportUrl = '/cms/public/statisticsSummaryFront/insert';
const analytics = new Analytics(defaultReportUrl);

/**
 * 通用埋点方法
 * @param {Object} options - 埋点参数
 * @param {string} [options.deviceId] - 设备标识
 * @param {string|number} [options.userId] - 登录用户 ID
 * @param {string} [options.companyCode] - 公司编码
 * @param {string} [options.companyName] - 公司名称
 * @param {string} [options.module] - 模块名称
 * @param {string} [options.eventCode] - 事件编码
 * @param {string} [options.eventName] - 事件名称
 * @param {string} [options.route] - 路由
 * @param {string} [options.attrName] - 属性名称
 * @param {string} [options.attrCode] - 属性值
 * @param {number} [options.sourceSign] - 入口来源：1 网页端，2 移动端，3 cmdc 入口
 * @param {Object} [options.extra] - 其他扩展字段，会合并到上报数据
 * @returns {Promise}
 */
export async function trackEvent(options) {
	const payload = { ...options };
	if (payload.extra) {
		Object.assign(payload, payload.extra);
		delete payload.extra;
	}

	if (import.meta.client) {
		if (!trackingDeviceIdPromise) {
			trackingDeviceIdPromise = getPersistentDeviceID();
		}
		payload.deviceId = await trackingDeviceIdPromise;

		const isLogin = !!getStorageSync('cmdc_access_token_mobile');
		const userInfo = isLogin ? getStorageSync('userInfo') || getStorageSync('user') || {} : {};
		if (isLogin && userInfo?.userId != null && userInfo.userId !== '') {
			payload.userId = userInfo.userId;
		} else {
			delete payload.userId;
		}

		// 未显式传站点名称时，按 companyCode 从项目现有站点缓存补齐。
		if (!payload.companyName && payload.companyCode) {
			const cachedSiteList = getStorageSync('getMutilAll') || getStorageSync('getMutil') || [];
			const currentSite = Array.isArray(cachedSiteList)
				? cachedSiteList.find(item => String(item?.companyCode || '') === String(payload.companyCode))
				: null;
			payload.companyName = currentSite?.websiteName || currentSite?.companyName || '';
		}
	}

	payload.companyName = payload.companyName || '';
	return analytics.sendData(payload);
}

/**
 * 创建使用自定义上报地址的埋点实例（如需多端/多接口时使用）
 * @param {string} url - 上报 API 地址
 * @returns {{ trackEvent: (options: Object) => Promise }}
 */
export function createAnalytics(url) {
	const instance = new Analytics(url);
	return {
		trackEvent(options) {
			return instance.sendData(options);
		},
	};
}

export { Analytics };
